import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isAxiosError } from "axios";
import {
  InvoiceWaste,
  ReconcileDetailsRequest,
  UpdateReconcileDetailsRequest,
} from "../../../types/invoice-waste";
import { getInvoiceWaste, updateReconcileDetails } from "../../../services";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import { SxProps, Theme } from "@mui/material/styles";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";
import { pushGlobalError } from "../../../errors/globalError";
import { mapReconcileConflictMessage } from "./reconcile";
import Tooltip from "@mui/material/Tooltip";
import FixWasteSkeleton from "./FixWasteSkeleton";
import { sumInvoiceWasteColumns } from "./invoiceWasteTotals";
import { numberWithCommas } from "../../../utils";

const compact = "@media " + COMPACT_QUERY;

// Highlighted columns (เหลือ / เสีย) use an inset box-shadow instead of `border`.
// In a border-collapse table the shared left/right edge of these header cells is
// also claimed by the neighbor cells' borderRight (draft color), which wins the
// edge and drops the colored border. An inset box-shadow is independent of border
// collapsing, so it always draws a complete colored ring on all four sides.
const yellowBg = "#FFE9411A";
const redBg = "#FB3B3B1A";
const yellowHighlight = {
  boxShadow: "inset 0 0 0 1px #FFE941",
  bgcolor: yellowBg,
};
const redHighlight = {
  boxShadow: "inset 0 0 0 1px #FB3B3B",
  bgcolor: redBg,
};

// Columns from "ยกมา" through "ควรเบิก" share one equal width.
const COL_WIDTH = 96;
const INDEX_COL_WIDTH = 56;
const NAME_COL_WIDTH = 220;
const EQUAL_WIDTH_COLS = [
  "carryover_qty",
  "receive_withdraw_qty",
  "actual_remain_qty",
  "receive_remain_qty",
  "receive_waste_qty",
  "actual_waste_qty",
  "transfer_in",
  "transfer_out",
  "defective",
  "actual_sell_qty",
  "sell_pda_qty",
  "fine",
  "should_withdraw_qty",
];
const TABLE_MIN_WIDTH =
  INDEX_COL_WIDTH + NAME_COL_WIDTH + EQUAL_WIDTH_COLS.length * COL_WIDTH;

/**
 * The แถวสรุปยอด row while editing.
 *
 * Pinned ABOVE the fixed save bar rather than at bottom: 0 — this page ends in a 66px
 * action bar (52px when compact), and a totals row at the very bottom would slide
 * underneath it and be unreadable at exactly the moment it matters.
 */
const TOTALS_ROW_SX = {
  position: "sticky" as const,
  bottom: "66px",
  zIndex: 2,
  [compact]: { bottom: "52px" },
  "& td": {
    bgcolor: (theme: Theme) => theme.palette.background.default,
    borderTop: "2px solid",
    borderTopColor: "divider",
    borderBottom: "none",
    fontWeight: 700,
    whiteSpace: "nowrap",
    padding: "8px 4px",
  },
};

// Fields the PUT /reconciles/{uuid}/details endpoint accepts as editable.
const EDITABLE_FIELDS = [
  "actual_carryover_qty",
  "actual_withdraw_qty",
  "actual_remain_qty",
  "actual_waste_qty",
  "transfer_in",
  "transfer_out",
  "defective",
] as const;

const toNumberOrUndefined = (value: number | string | null | undefined) =>
  value === "" || value === null || value === undefined
    ? undefined
    : Number(value);

interface FixWasteTableProps {
  version?: string;
}

export default function FixWasteTable({ version }: FixWasteTableProps) {
  const navigate = useNavigate();
  const { reportId, checkInId } = useParams<{
    reportId: string;
    checkInId: string;
  }>();
  const [invoiceWaste, setInvoiceWaste] = useState<InvoiceWaste[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const focusCell = (field: keyof InvoiceWaste, rowIndex: number) => {
    const next = inputRefs.current[`${String(field)}:${rowIndex}`];
    if (!next) return;
    next.focus();
    next.select();
  };

  const handleInputKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    field: keyof InvoiceWaste,
    rowIndex: number
  ) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    focusCell(field, rowIndex + 1);
  };

  const onGetInvoiceWaste = useCallback(
    async (uuid: string, version: string) => {
      setLoading(true);
      try {
        const { data: body } = await getInvoiceWaste(uuid, version);
        setInvoiceWaste(body.data ?? []);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (reportId && version) onGetInvoiceWaste(reportId, version);
  }, [reportId, version, onGetInvoiceWaste]);

  const handleChange = (
    product_uuid: string,
    field: keyof InvoiceWaste,
    value: string
  ) => {
    setInvoiceWaste((prev) =>
      prev.map((item) =>
        item.product_uuid === product_uuid ? { ...item, [field]: value } : item
      )
    );
  };

  const onUpdateReconcileDetails = useCallback(async () => {
    if (!reportId || !checkInId) return;

    const products: ReconcileDetailsRequest[] = invoiceWaste.map((product) => {
      const entry: ReconcileDetailsRequest = {
        product_uuid: product.product_uuid,
      };
      EDITABLE_FIELDS.forEach((field) => {
        const value = toNumberOrUndefined(product[field]);
        if (value !== undefined) entry[field] = value;
      });
      return entry;
    });

    const request: UpdateReconcileDetailsRequest = { products };
    setSaving(true);
    try {
      await updateReconcileDetails(checkInId, request);
      navigate(`/report/sale/details/${reportId}?tab=invoice`);
    } catch (error) {
      // 409 = the reconcile is no longer editable (status changed since the page
      // loaded). Replace the raw backend message with a friendly one, then go
      // back to the detail page, which re-fetches the reconcile/report status so
      // the edit button reflects the current, non-editable state. This also
      // avoids leaving the user on a stale editable page.
      if (isAxiosError(error) && error.response?.status === 409) {
        const backendMessage = error.response.data?.message as
          | string
          | undefined;
        pushGlobalError(mapReconcileConflictMessage(backendMessage));
        navigate(`/report/sale/details/${reportId}?tab=invoice`);
        return;
      }
      // Other errors are already surfaced by the global axios error handler.
    } finally {
      setSaving(false);
    }
  }, [reportId, checkInId, invoiceWaste, navigate]);

  // Recomputed on every keystroke: the point of a totals row on an EDIT screen is
  // watching the total move as you correct a figure.
  const totals = sumInvoiceWasteColumns(invoiceWaste);

  const headCellSx: SxProps<Theme> = {
    borderRight: (theme) => `1px solid ${theme.palette.draft.main}`,
    bgcolor: (theme) => theme.palette.background.default,
  };

  const renderEditableCell = (
    product: InvoiceWaste,
    field: keyof InvoiceWaste,
    rowIndex: number,
    cellSx?: SxProps<Theme>
  ) => (
    <TableCell align="center" sx={cellSx}>
      <TextField
        size="small"
        type="number"
        value={product[field] ?? ""}
        onChange={(e) => handleChange(product.product_uuid, field, e.target.value)}
        onKeyDown={(e) => handleInputKeyDown(e, field, rowIndex)}
        inputRef={(el: HTMLInputElement | null) => {
          inputRefs.current[`${String(field)}:${rowIndex}`] = el;
        }}
        sx={{ width: 70, "& input": { textAlign: "center" } }}
      />
    </TableCell>
  );

  // ยกมา: ช่องเดียวที่แก้ได้. input ผูกกับ actual_carryover_qty (ค่าที่การเงินกรอกเอง,
  // ส่งผ่าน PUT details); ค่าระบบปัจจุบัน (receive_carryover ?? transfer) แสดงเป็น
  // placeholder ไว้เป็นไกด์. ไม่กรอก → input ว่าง → payload ไม่ส่ง field นี้ →
  // BE ใช้ค่าระบบต่อ. ล้างค่าเป็นว่าง = กลับไปใช้ค่าระบบเช่นกัน.
  const renderCarryoverCell = (product: InvoiceWaste, rowIndex: number) => {
    const field: keyof InvoiceWaste = "actual_carryover_qty";
    const systemValue = product.receive_carryover_qty ?? product.transfer_qty;
    return (
      <TableCell align="center">
        <TextField
          size="small"
          type="number"
          value={product.actual_carryover_qty ?? ""}
          placeholder={String(systemValue)}
          onChange={(e) =>
            handleChange(product.product_uuid, field, e.target.value)
          }
          onKeyDown={(e) => handleInputKeyDown(e, field, rowIndex)}
          inputRef={(el: HTMLInputElement | null) => {
            inputRefs.current[`${String(field)}:${rowIndex}`] = el;
          }}
          sx={{ width: 70, "& input": { textAlign: "center" } }}
        />
      </TableCell>
    );
  };

  // เบิก: ช่องเดียวที่แก้ได้. แสดงค่า effective (actual_withdraw ?? PDA) — ต่างจาก
  // ยกมาตรงที่ actual_withdraw_qty มีค่าเสมอ (checker-confirmed) จึง bind ค่าตรง ๆ
  // ไม่ใช้ placeholder. แก้แล้วเขียนเฉพาะ actual_withdraw_qty (ส่งผ่าน PUT details,
  // BE ใช้คิด actual_sell_qty / fine ใหม่).
  const renderWithdrawCell = (product: InvoiceWaste, rowIndex: number) => {
    const field: keyof InvoiceWaste = "actual_withdraw_qty";
    const displayValue =
      product.actual_withdraw_qty ?? product.receive_withdraw_qty;
    return (
      <TableCell align="center">
        <TextField
          size="small"
          type="number"
          value={displayValue ?? ""}
          onChange={(e) =>
            handleChange(product.product_uuid, field, e.target.value)
          }
          onKeyDown={(e) => handleInputKeyDown(e, field, rowIndex)}
          inputRef={(el: HTMLInputElement | null) => {
            inputRefs.current[`${String(field)}:${rowIndex}`] = el;
          }}
          sx={{ width: 70, "& input": { textAlign: "center" } }}
        />
      </TableCell>
    );
  };

  if (loading) return <FixWasteSkeleton />;

  return (
    <>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", minWidth: TABLE_MIN_WIDTH }}>
          <colgroup>
            <col style={{ width: INDEX_COL_WIDTH }} />
            <col style={{ width: NAME_COL_WIDTH }} />
            {EQUAL_WIDTH_COLS.map((key) => (
              <col key={key} style={{ width: COL_WIDTH }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={headCellSx}>
                ลำดับ
              </TableCell>
              <TableCell align="left" sx={headCellSx}>
                รายการ
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                ยกมา
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                เบิก
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  ...yellowHighlight,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                เหลือ
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                เหลือ (PDA)
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                เสีย (PDA)
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  ...redHighlight,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                เสีย
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                โอนเข้า
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                โอนออก
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                ชำรุด
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                ขายได้
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                ขาย (PDA)
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                ค่าปรับ
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                ควรเบิก
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoiceWaste.map((product, index) => (
              <TableRow
                key={product.product_uuid}
                sx={{
                  bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                  "& td, & th": { padding: "4px" },
                }}
              >
                <TableCell align="center">{index + 1}</TableCell>

                <TableCell align="left">{product.product_name}</TableCell>

                {/* ยกมา — ช่องเดียว แสดงค่า effective และแก้ได้ */}
                {renderCarryoverCell(product, index)}

                {/* เบิก — ช่องเดียว แสดง effective (actual ?? PDA) และแก้ได้ */}
                {renderWithdrawCell(product, index)}

                {/* เหลือ (editable) / เหลือ PDA / เสีย PDA / เสีย (editable) */}
                {renderEditableCell(
                  product,
                  "actual_remain_qty",
                  index,
                  yellowHighlight
                )}
                <TableCell align="center">
                  {product.receive_remain_qty ?? "-"}
                </TableCell>
                <TableCell align="center">
                  {product.receive_waste_qty ?? "-"}
                </TableCell>
                {renderEditableCell(
                  product,
                  "actual_waste_qty",
                  index,
                  redHighlight
                )}

                {/* Transfer & defective (editable) */}
                {renderEditableCell(product, "transfer_in", index)}
                {renderEditableCell(product, "transfer_out", index)}
                {renderEditableCell(product, "defective", index)}

                {/* Server-derived (read-only) */}
                <TableCell align="center">{product.actual_sell_qty ?? "-"}</TableCell>
                <TableCell align="center">{product.sell_pda_qty ?? "-"}</TableCell>
                <TableCell align="center">{product.fine ?? "-"}</TableCell>
                <TableCell align="center">
                  {product.should_withdraw_qty}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          {invoiceWaste.length > 0 && (
            <TableFooter>
              <TableRow sx={TOTALS_ROW_SX}>
                <TableCell align="center">รวม</TableCell>
                <TableCell align="left">
                  {numberWithCommas(invoiceWaste.length)} รายการ
                </TableCell>
                <TableCell align="center">{numberWithCommas(totals.carryover)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.withdraw)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.remain)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.remain_pda)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.waste_pda)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.waste)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.transfer_in)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.transfer_out)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.defective)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.sell)}</TableCell>
                <TableCell align="center">{numberWithCommas(totals.sell_pda)}</TableCell>
                <TableCell align="center">
                  {/* Left blank on purpose. ค่าปรับ is netted per price group from
                      ขายได้, which the server re-derives from these counts on save —
                      so any figure shown here would be answering for the numbers as
                      they were BEFORE the edit being typed. */}
                  <Tooltip title="ยอดหักคำนวณใหม่หลังกดอัพเดทข้อมูล">
                    <span>-</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  {numberWithCommas(totals.should_withdraw)}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      <Paper
        sx={{
          // Offset by the live sidebar width so this fixed bar stays aligned
          // with the main content area instead of sliding under the sidebar.
          left: "var(--gb-sidebar-width, 0px)",
          right: 0,
          height: "66px",
          position: "fixed",
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "24px",
          boxShadow: "0 -4px 24px -8px rgba(0,0,0,0.10)",
          [compact]: {
            height: "52px",
            padding: "8px 16px",
          },
        }}
      >
        <Button
          size="large"
          variant="contained"
          color="primary"
          disabled={saving}
          onClick={onUpdateReconcileDetails}
        >
          อัพเดทข้อมูล
        </Button>
      </Paper>
    </>
  );
}
